import { useState } from "react";
import { api } from "../config/api";
import { Icon } from "./Icon.jsx";

export function RestockRequestModal({ product, storeId, onClose, onSuccess, customer = null }) {
  const [name, setName] = useState(customer?.name || "");
  const [instagram, setInstagram] = useState(customer?.instagram || "");
  const [email, setEmail] = useState(customer?.email || "");
  const [phone, setPhone] = useState(customer?.phone || "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    // Email is how we tell them the moment it's back — required (it used to be optional, so most requesters never got told).
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
      setError("Please enter a valid email — we'll email you as soon as it's back in stock");
      return;
    }
    if (!instagram.trim()) {
      setError("Instagram handle is required — it's how the store reaches you when it's back");
      return;
    }

    setLoading(true);
    setError("");
    const res = await api.submitRestockRequest(
      storeId,
      product.id,
      name.trim(),
      phone.trim() || null,
      message.trim() || null,
      customer?.id || null,
      email.trim() || null,
      instagram.trim().replace(/^@/, "") || null
    );

    if (res.error) {
      setError(res.error);
      setLoading(false);
    } else {
      setSuccess(true);
      setLoading(false);
      onSuccess?.();
      setTimeout(() => onClose(), 2000);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={styles.overlay}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={styles.modal}>
        {!success ? (
          <>
            <div style={styles.header}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>Request Restock</h2>
              <button onClick={onClose} style={styles.closeBtn}>✕</button>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Product</label>
                <p style={{ margin: 0, fontSize: "14px", color: "var(--text2)" }}>{product.name}</p>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Your Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  style={styles.input}
                  disabled={loading}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Instagram handle *</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-soft)", fontWeight: 700 }}>@</span>
                  <input
                    type="text"
                    value={instagram.replace(/^@/, "")}
                    onChange={(e) => setInstagram(e.target.value)}
                    placeholder="yourhandle"
                    style={{ ...styles.input, paddingLeft: 26 }}
                    disabled={loading}
                  />
                </div>
                <p style={{ margin: "5px 0 0", fontSize: "11px", color: "var(--ink-soft)" }}>Required — this is how the store reaches you when it's back in stock.</p>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={styles.input}
                  disabled={loading}
                />
                <p style={{ margin: "5px 0 0", fontSize: "11px", color: "var(--ink-soft)" }}>We'll email you the moment it's back in stock.</p>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Your Phone (optional)</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  style={styles.input}
                  disabled={loading}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Message (optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Any comments? (e.g., 'Urgent' or 'Need by weekend')"
                  style={styles.textarea}
                  disabled={loading}
                  rows={3}
                />
              </div>

              {error && (
                <div style={styles.error}>{error}</div>
              )}

              <div style={styles.actions}>
                <button
                  type="button"
                  onClick={onClose}
                  style={styles.cancelBtn}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.submitBtn}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Request"}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div style={styles.successContainer}>
            <div style={styles.successIcon}><Icon n="done" size={24} /></div>
            <h3 style={{ margin: "16px 0 8px", fontSize: "16px", fontWeight: "600" }}>Request Sent!</h3>
            <p style={{ margin: 0, fontSize: "13px", color: "var(--text2)" }}>
              {customer?.email
                ? "We'll email you the moment it's back in stock."
                : "The store owner will be notified when it's back in stock."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "var(--overlay)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "var(--white)",
    borderRadius: "4px",
    width: "90%",
    maxWidth: "450px",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "var(--shadow)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderBottom: "1px solid var(--border)",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "var(--ink-soft)",
    padding: "0",
  },
  form: {
    padding: "20px",
  },
  field: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "var(--ink)",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid var(--border)",
    borderRadius: "4px",
    fontSize: "14px",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid var(--border)",
    borderRadius: "4px",
    fontSize: "14px",
    fontFamily: "inherit",
    boxSizing: "border-box",
    resize: "vertical",
  },
  error: {
    background: "var(--red-light)",
    color: "var(--red)",
    padding: "10px 12px",
    borderRadius: "4px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  actions: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
  },
  cancelBtn: {
    flex: 1,
    padding: "10px 16px",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  submitBtn: {
    flex: 1,
    padding: "10px 16px",
    background: "var(--ink)",
    color: "var(--on-ink)",
    border: "none",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  successContainer: {
    padding: "40px 20px",
    textAlign: "center",
  },
  successIcon: {
    width: "50px",
    height: "50px",
    background: "var(--green-bg)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    color: "var(--green)",
    margin: "0 auto",
  },
};
